import { Component, EventEmitter, Output, computed, input, InputSignal, Signal } from '@angular/core';
import { UploadPhotoComponent } from '../upload-photo/upload-photo.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProductCard } from '../../../product/models/product-card';
import { ProductTypes } from '../../../product/models/models';
import { UnitConverter } from '../../utils/unitConverter';
import { DefaultProductParams } from '../../models/defaultProductParams';
import { SHARED_MODULES } from '../../../shared.';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';

export type ImageSelectVariant = 'default' | 'product' | 'preview';

@Component({
    selector: 'app-image-select',
    templateUrl: 'image-select.component.html',
    styleUrls: ['image-select.component.scss'],
	imports: [SHARED_MODULES, SanitizerPipe, PhIconComponent, PhIconWrapperComponent, PhButtonComponent],
    standalone: true
})
export class ImageSelectComponent {
	private readonly _defaultWidth: number = UnitConverter.getConvertValue(new DefaultProductParams().width);
	private readonly _defaultHeight: number = UnitConverter.getConvertValue(new DefaultProductParams().height);

	readonly variant: InputSignal<ImageSelectVariant> = input<ImageSelectVariant>('default');
	readonly title: InputSignal<string> = input<string>('');
	readonly addButtonLabel: InputSignal<string> = input<string>('ADD_IMAGE');

	readonly image: InputSignal<string> = input<string>('');
	readonly modalTitle: InputSignal<string> = input<string>('');
	readonly product: InputSignal<ProductCard> = input<ProductCard>(null);

	readonly widthInput: InputSignal<number> = input<number>(null, { alias: 'width' });
	readonly heightInput: InputSignal<number> = input<number>(null, { alias: 'height' });

	readonly canEdit: InputSignal<boolean> = input<boolean>(true);
	readonly disabled: InputSignal<boolean> = input<boolean>(false);
	readonly label: InputSignal<string> = input<string>('PRODUCT_CARD.CHANGE_PHOTO');
	readonly containerHeight: InputSignal<number> = input<number>(112);
	readonly containerWidth: InputSignal<number> = input<number>(180);
	readonly opacity: InputSignal<number> = input<number>(1);

	readonly effectiveWidth: Signal<number> = computed((): number => {
		const widthFromInput: number = this.widthInput();
		if (typeof widthFromInput === 'number' && widthFromInput > 0) return widthFromInput;

		const widthFromProduct: number = this.product()?.params?.width;
		if (typeof widthFromProduct === 'number' && widthFromProduct > 0) return widthFromProduct;

		return this._defaultWidth;
	});

	readonly effectiveHeight: Signal<number> = computed((): number => {
		const heightFromInput: number = this.heightInput();
		if (typeof heightFromInput === 'number' && heightFromInput > 0) return heightFromInput;

		const heightFromProduct: number = this.product()?.params?.height;
		if (typeof heightFromProduct === 'number' && heightFromProduct > 0) return heightFromProduct;

		return this._defaultHeight;
	});

	@Output() edit: EventEmitter<string> = new EventEmitter<string>();

	constructor(
		private dialog: MatDialog
	) {}

	change(): void {
		this.configureProduct();
	}

	delete(): void {
		this.edit.emit(null);
	}

	configureProduct(): void {
		const product: ProductCard = this.product();
		const dialogRef: MatDialogRef<UploadPhotoComponent, any> = this.dialog.open(UploadPhotoComponent, {
			width: '724px',
			autoFocus: false,
			backdropClass: "back-drop",
			data: {
				title: this.modalTitle(),
				product: product
					?
					{
						id: product.id,
						name: product.name,
						article: product.params.article,
						barcode: product.params.barcode,
						isMasterPosition: product.type === ProductTypes.MasterPosition,
						width: this.effectiveWidth(),
						height: this.effectiveHeight()
					}
					: null,
				photo: this.image(),
				allowInternet: product?.type !== ProductTypes.MasterPosition
			}
		});

		dialogRef.afterClosed().subscribe(async (result: any): Promise<void> => {
			if (result === false || typeof result === 'undefined') return;

			this.edit.emit(result);
		});
	}

	readonly previewContentSize: Signal<{ width: number; height: number }> = computed((): { width: number; height: number } => {
		const frameWidthRaw: number = this.containerWidth();
		const frameHeightRaw: number = this.containerHeight();
		const frameWidth: number = frameWidthRaw > 0 ? frameWidthRaw : 140;
		const frameHeight: number = frameHeightRaw > 0 ? frameHeightRaw : 140;

		const productWidth: number = this.effectiveWidth();
		const productHeight: number = this.effectiveHeight();
		const variant: ImageSelectVariant = this.variant();

		if (variant !== 'preview' || productWidth <= 0 || productHeight <= 0) {
			return { width: frameWidth, height: frameHeight };
		}

		const scale: number = Math.min(frameWidth / productWidth, frameHeight / productHeight);
		return {
			width: Math.max(1, Math.round(productWidth * scale)),
			height: Math.max(1, Math.round(productHeight * scale))
		};
	});

	readonly previewContentWidth: Signal<number> = computed((): number => this.previewContentSize().width);
	readonly previewContentHeight: Signal<number> = computed((): number => this.previewContentSize().height);

	readonly imageContainerStyles: Signal<Record<string, string>> = computed(
		(): Record<string, string> => ({
			'--image-select-container-width': `${this.containerWidth()}px`,
			'--image-select-container-height': `${this.containerHeight()}px`,
			'--image-select-preview-width': `${this.previewContentWidth()}px`,
			'--image-select-preview-height': `${this.previewContentHeight()}px`
		})
	);
}
